import { useEffect, useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { Filter } from "lucide-react"
import * as outreachApi from "../api/outreach.api"
import type { CampaignFunnel as Funnel } from "../types/outreach.types"

const MAIN: { key: Exclude<keyof Funnel, "campaign_id">; label: string }[] = [
  { key: "enrolled", label: "Enrolled" },
  { key: "intro_texted", label: "Intro texted" },
  { key: "replied", label: "Replied" },
  { key: "preference_captured", label: "Gave a time" },
  { key: "booked", label: "Call booked" },
  { key: "called", label: "Called" },
  { key: "consented", label: "Consented" },
]

const OFF_RAMP: { key: Exclude<keyof Funnel, "campaign_id">; label: string }[] = [
  { key: "refused", label: "Declined" },
  { key: "max_attempts", label: "Out of attempts" },
  { key: "not_my_patient", label: "Not our patient" },
  { key: "paused", label: "Paused" },
]

/** GET /outreach/org/{org}/campaigns/{id}/funnel */
export function CampaignFunnel({ orgId, campaignId, refreshKey }: { orgId: string; campaignId: string; refreshKey?: number }) {
  const [funnel, setFunnel] = useState<Funnel | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setError(null)
    outreachApi
      .getCampaignFunnel(orgId, campaignId)
      .then((f) => !cancelled && setFunnel(f))
      .catch((err) => !cancelled && setError(outreachApi.errorMessage(err)))
    return () => {
      cancelled = true
    }
  }, [orgId, campaignId, refreshKey])

  const base = funnel?.enrolled || 0
  const pct = (n: number) => (base ? Math.round((n / base) * 100) : 0)

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Filter className="h-4 w-4" />
          Outreach funnel
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {error ? (
          <p className="text-sm text-muted-foreground">Funnel unavailable: {error}</p>
        ) : !funnel ? (
          <Skeleton className="h-24" />
        ) : (
          <>
            <div className="space-y-1.5">
              {MAIN.map(({ key, label }) => (
                <div key={key} className="grid grid-cols-[8rem,1fr,4.5rem] items-center gap-2 text-sm">
                  <span className="text-muted-foreground">{label}</span>
                  <div className="h-2 overflow-hidden rounded-full bg-muted">
                    <div className="h-full rounded-full bg-primary" style={{ width: `${pct(funnel[key] || 0)}%` }} />
                  </div>
                  <span className="text-right font-medium tabular-nums">
                    {funnel[key] || 0}
                    <span className="ml-1 text-xs text-muted-foreground">{pct(funnel[key] || 0)}%</span>
                  </span>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 border-t pt-3 text-sm">
              {OFF_RAMP.map(({ key, label }) => (
                <span key={key} className="text-muted-foreground">
                  {label} <span className="font-medium text-foreground tabular-nums">{funnel[key] || 0}</span>
                </span>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
